import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useTranslation } from '../../hooks/useTranslation';
import { profileAPI } from '../../services/api';
import { useToast } from '../ui/use-toast';

const BrandingTab = ({ profile, onUpdate }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    invoice_prefix: profile?.branding?.invoice_prefix || 'INV',
    payment_terms: profile?.branding?.payment_terms || 'Net 30',
    signature: profile?.branding?.signature || '',
    legal_mentions: profile?.branding?.legal_mentions || '',
    primary_color: profile?.branding?.primary_color || '#2563EB',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await profileAPI.updateBranding(formData);
      toast({
        title: t('common.success'),
        description: 'Branding settings updated successfully',
      });
      onUpdate();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.detail || 'Failed to update branding settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{t('profile.branding.title')}</h3>
        <p className="text-sm text-gray-500 mt-1">{t('profile.branding.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="invoice_prefix">{t('profile.branding.invoicePrefix')}</Label>
            <Input
              id="invoice_prefix"
              name="invoice_prefix"
              value={formData.invoice_prefix}
              onChange={handleChange}
              className="input-premium mt-1"
              placeholder="INV"
            />
            <p className="text-xs text-gray-500 mt-1">Example: INV-00001</p>
          </div>

          <div>
            <Label htmlFor="payment_terms">{t('profile.branding.paymentTerms')}</Label>
            <Input
              id="payment_terms"
              name="payment_terms"
              value={formData.payment_terms}
              onChange={handleChange}
              className="input-premium mt-1"
              placeholder="Net 30"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="primary_color">{t('profile.branding.primaryColor')}</Label>
          <div className="flex items-center space-x-4 mt-1">
            <input
              type="color"
              id="primary_color"
              name="primary_color"
              value={formData.primary_color}
              onChange={handleChange}
              className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
            />
            <Input
              value={formData.primary_color}
              onChange={handleChange}
              name="primary_color"
              className="input-premium flex-1"
              placeholder="#2563EB"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="signature">{t('profile.branding.signature')}</Label>
          <Textarea
            id="signature"
            name="signature"
            value={formData.signature}
            onChange={handleChange}
            className="input-premium mt-1"
            rows={3}
            placeholder="Best regards,\nYour Name\nYour Company"
          />
        </div>

        <div>
          <Label htmlFor="legal_mentions">{t('profile.branding.legalMentions')}</Label>
          <Textarea
            id="legal_mentions"
            name="legal_mentions"
            value={formData.legal_mentions}
            onChange={handleChange}
            className="input-premium mt-1"
            rows={4}
            placeholder="Company registration, VAT number, legal information..."
          />
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={saving} className="btn-premium">
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BrandingTab;
